import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Card, Form, Input, InputNumber, Button, Upload, Switch, message, Space, Modal, Select, Alert, Spin } from 'antd';
import { UploadOutlined, PlusOutlined, MinusCircleOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import type { Product } from '../../types/product';
import request from '../../utils/request';
import { useAuth } from '../../hooks/useAuth';
import { getCustomerList } from '../../services/project';
import { HelpHintButton } from '../../components/HelpHintButton';

const { TextArea } = Input;

function showSortingBarcodeHelpModal() {
  Modal.info({
    title: '分拣条码说明',
    width: 640,
    okText: '知道了',
    icon: null,
    content: (
      <div className="text-gray-700" style={{ lineHeight: 1.7 }}>
        <p style={{ marginBottom: 12 }}>
          分拣条码指的是产品上粘贴的条形码标签(barcode),如亚马逊的FNSKU标签等;
        </p>
        <p style={{ marginBottom: 12 }}>
          每款产品可以对应多个分拣条码,每件产品上只能粘贴一个有效的分拣条码,若没有条码可能会影响正常出入库;
        </p>
        <p style={{ marginBottom: 0 }}>
          同一个客户的不同产品,可以有相同的分拣条码,但添加至同一个项目的不同产品,不能有相同的分拣条码
        </p>
      </div>
    ),
  });
}

function showLogisticsPackageHelpModal() {
  Modal.info({
    title: '自带物流包装说明',
    width: 560,
    okText: '知道了',
    icon: null,
    content: (
      <div className="text-gray-700" style={{ lineHeight: 1.7 }}>
        <p style={{ marginBottom: 12 }}>
          打开此开关表示产品自带物流包装,则发货时无需另外装箱/装袋(物流塑胶袋);
        </p>
        <p style={{ marginBottom: 0 }}>
          若无物流包装,仓库出库时必须执行装箱/装袋操作,会产生额外费用
        </p>
      </div>
    ),
  });
}

/** 编辑时可提交的字段（已进入业务的产品只提交描述、图片和分拣条码） */
const EDITABLE_FIELDS = ['name', 'custom_code', 'description', 'value_usd', 'weight_grams', 'length_cm', 'width_cm', 'height_cm', 'has_logistics_package'];

const roleBasePath = (role?: string) =>
  role === 'admin' ? '/dashboard/admin' : role === 'staff' ? '/dashboard/staff' : '/dashboard/customer';

/** 保存成功后回到产品列表，并关闭当前（编辑）标签页 */
const backToProductList = (navigate: ReturnType<typeof useNavigate>, role?: string) => {
  const tabsManager = (window as any).tabsManager;
  const currentTabKey = tabsManager && typeof tabsManager.getActiveTab === 'function' ? tabsManager.getActiveTab() : null;
  navigate(`${roleBasePath(role)}/products/list`);
  setTimeout(() => {
    if (tabsManager && currentTabKey) tabsManager.closeTab(currentTabKey);
  }, 1000);
};

const CreateProduct: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth(); // 获取当前用户信息
  // 路由 /products/:id/edit 为编辑模式，复用创建页表单
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(isEdit);
  /** 已进入业务的产品：只能改描述、图片，分拣条码只能新增 */
  const editLimited = isEdit && Boolean(editingProduct?.edit_limited);
  
  // 🔑 关键修改：从URL查询参数获取returnPath
  const searchParams = new URLSearchParams(location.search);
  const returnPath = searchParams.get('returnPath');
  const source = searchParams.get('source');
  
  // 页面内状态管理，完全绕过TabsManager
  const [navigationContext] = useState(() => ({
    returnPath: returnPath ? decodeURIComponent(returnPath) : null,
    source: source || null,
    timestamp: new Date().toISOString()
  }));

  // 从“项目基础配置-创建页”跳转过来的专用回跳标记
  const isInventoryConfigReturn = Boolean(
    navigationContext.source === 'inventory-config' &&
    navigationContext.returnPath &&
    navigationContext.returnPath.includes('/projects/inventory-config/create')
  );
  
  // 添加调试日志
  console.log('🚀 产品创建页面加载:', {
    urlSearch: location.search,
    returnPath: navigationContext.returnPath,
    source: navigationContext.source,
    currentPath: location.pathname,
    hasReturnPath: !!navigationContext.returnPath,
    timestamp: navigationContext.timestamp
  });
  
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>();
  const [uploadedPhoto, setUploadedPhoto] = useState<File | null>(null);
  const [customerOptions, setCustomerOptions] = useState<Array<{ value: number; label: string }>>([]);
  const [customerOptionsLoading, setCustomerOptionsLoading] = useState(false);

  const needCustomerField = !isEdit && (user?.role === 'admin' || user?.role === 'staff');

  // 编辑模式：读取产品并填入表单
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      setLoadingProduct(true);
      try {
        const { data } = await request.get<Product>(`/api/v1/products/${editId}/`);
        if (cancelled) return;
        setEditingProduct(data);
        form.setFieldsValue({
          name: data.name,
          custom_code: data.custom_code,
          description: data.description || '',
          value_usd: data.value_usd != null ? Number(data.value_usd) : undefined,
          weight_grams: data.weight_grams != null ? Number(data.weight_grams) : undefined,
          length_cm: data.length_cm != null ? Number(data.length_cm) : undefined,
          width_cm: data.width_cm != null ? Number(data.width_cm) : undefined,
          height_cm: data.height_cm != null ? Number(data.height_cm) : undefined,
          has_logistics_package: Boolean(data.has_logistics_package),
          sorting_codes: (data.sorting_codes || []).map((sc) => ({ code: sc.code, description: sc.description || '', existing: true })),
        });
        setImageUrl(data.photo || undefined);
        try {
          sessionStorage.setItem(`tab_title_product_edit:${window.location.pathname}`, data.custom_code || '');
        } catch {
          // 静默失败
        }
      } catch (error: any) {
        if (!cancelled) message.error(error?.response?.data?.detail || '获取产品信息失败');
      } finally {
        if (!cancelled) setLoadingProduct(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, editId, form]);

  // 编辑保存：PATCH，只提交可改的字段；分拣条码整体提交（已进入业务的产品后端只允许新增）
  const handleUpdate = async (values: any) => {
    if (!editingProduct) return;
    const formData = new FormData();
    const fields = editLimited ? ['description'] : EDITABLE_FIELDS;
    fields.forEach((key) => {
      const v = values[key];
      if (v === undefined || v === null) return;
      formData.append(key, typeof v === 'string' ? v.trim() : String(v));
    });
    const codes = (values.sorting_codes || [])
      .filter((item: any) => item && item.code && String(item.code).trim())
      .map((item: any) => ({
        code: String(item.code).trim().toUpperCase(),
        description: typeof item.description === 'string' ? item.description.trim() : String(item.description ?? '').trim(),
      }));
    formData.append('sorting_codes', JSON.stringify(codes));
    if (uploadedPhoto) formData.append('photo', uploadedPhoto);

    const save = async () => {
      setLoading(true);
      try {
        await request.patch(`/api/v1/products/${editingProduct.id}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        message.success('保存成功');
        backToProductList(navigate, user?.role);
      } catch (error: any) {
        const data = error?.response?.data;
        if (data?.detail) {
          message.error(String(data.detail));
        } else if (data && typeof data === 'object') {
          Object.entries(data).forEach(([field, messages]) => {
            if (Array.isArray(messages)) message.error(`${field}: ${messages[0]}`);
          });
        } else {
          message.error('保存失败，请稍后重试');
        }
      } finally {
        setLoading(false);
      }
    };

    // 库存单元标签上只印产品名称和一个分拣条码：只有这两项变了，已打印的标签才会和系统不一致。
    // 标签取哪个分拣条码与条码的创建先后有关，新增或去掉条码都可能换掉标签上的那个，所以按条码集合是否变化判断；只改条码说明不影响。
    const nameChanged = String(values.name ?? '').trim() !== String(editingProduct.name ?? '').trim();
    const oldCodes = new Set((editingProduct.sorting_codes || []).map((sc) => String(sc.code || '').trim().toUpperCase()));
    const newCodes = new Set<string>(codes.map((c: { code: string }) => c.code));
    const codesChanged = oldCodes.size !== newCodes.size || Array.from(newCodes).some((c) => !oldCodes.has(c));
    const labelChanges = [nameChanged ? '产品名称' : '', codesChanged ? '分拣条码' : ''].filter(Boolean).join('、');

    if (editingProduct.usage_level === 'configured' && labelChanges) {
      Modal.confirm({
        title: '确认保存',
        content: `${labelChanges}已修改，已打印的库存单元标签上的${labelChanges}会与系统不一致，建议保存后重新打印标签。确定保存吗？`,
        okText: '保存',
        cancelText: '取消',
        onOk: save,
      });
      return;
    }
    await save();
  };

  useEffect(() => {
    if (!needCustomerField) return;
    let cancelled = false;
    (async () => {
      setCustomerOptionsLoading(true);
      try {
        const list = await getCustomerList({ onlyMain: true });
        if (cancelled) return;
        const opts = (list || []).map((c: { id: string; username?: string; name?: string; customer_code?: string }) => {
          const id = Number(c.id);
          const code = c.customer_code || '';
          const name = c.name || c.username || '';
          const label = code ? `${name}（${code}）` : name || `用户 ${id}`;
          return { value: id, label };
        });
        setCustomerOptions(opts);
      } catch {
        if (!cancelled) setCustomerOptions([]);
      } finally {
        if (!cancelled) setCustomerOptionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [needCustomerField]);
  
  // 额外调试：当前登录用户对象
  useEffect(() => {
    console.log('👤 当前用户对象(user):', user);
    console.log('👤 当前用户角色:', user?.role, '子账号父ID:', (user as any)?.profile?.parent_customer_id || null);
  }, [user]);

  // 处理表单提交
  const handleSubmit = async (values: any) => {
    if (isEdit) {
      await handleUpdate(values);
      return;
    }
    console.log('🚀 开始提交产品创建表单:', {
      formValues: values,
      navigationContext,
      hasPhoto: !!uploadedPhoto
    });
    
    setLoading(true);
    try {
      // 创建 FormData 对象
      const formData = new FormData();
      
      // 处理分拣条码字段：去首尾空格，条码转大写后保存（与后端一致）
      if (values.sorting_codes && values.sorting_codes.length > 0) {
        const validCodes = values.sorting_codes
          .filter((item: any) => item && item.code && String(item.code).trim())
          .map((item: any) => ({
            code: String(item.code).trim().toUpperCase(),
            description:
              typeof item.description === 'string' ? item.description.trim() : String(item.description ?? '').trim(),
          }));
        if (validCodes.length > 0) {
          formData.append('sorting_codes', JSON.stringify(validCodes));
          console.log('📋 添加分拣条码:', validCodes);
        }
      }
      
      // 添加其他字段
      Object.keys(values).forEach(key => {
        if (key !== 'photo' && key !== 'sorting_codes' && values[key] !== undefined) {
          formData.append(key, values[key]);
        }
      });

      // 🔑 关键修复：自动设置客户创建产品所需的必要字段
      if (user) {
        // 验证用户角色：只有客户用户才能使用此逻辑
        if (user.role === 'customer') {
          // 子账号需使用主账号ID作为 customer；主账号则使用自身ID
          const parentCustomerId = (user as any)?.profile?.parent_customer_id;
          const effectiveCustomerId = parentCustomerId ? parentCustomerId : user.id;

          // 客户创建产品时，自动设置：
          // customer = 有效客户ID（主账号ID）
          // created_by = 当前登录客户用户ID  
          // creation_source = 'customer'
          formData.append('customer', String(effectiveCustomerId));
          formData.append('created_by', String(user.id));
          formData.append('creation_source', 'customer');
          
          console.log('🔑 自动设置客户创建产品字段:', {
            customer_effective: effectiveCustomerId,
            parent_customer_id: parentCustomerId || null,
            created_by: user.id,
            creation_source: 'customer',
            userId: user.id,
            userRole: user.role
          });
          if (!parentCustomerId) {
            console.warn('⚠️ 未检测到 parent_customer_id，系统将使用自身ID作为 customer 提交');
          }
        } else if (user.role === 'admin' || user.role === 'staff') {
          const cid = values.customer;
          if (cid === undefined || cid === null || cid === '') {
            message.error('请选择所属客户');
            setLoading(false);
            return;
          }
          formData.append('created_by', String(user.id));
          // 管理员/员工代客户创建产品：统一归类为客户创建，并追加备注
          formData.append('creation_source', 'customer');
          const proxyCreateText = '由仓库代为创建';
          const currentDescription = typeof values.description === 'string' ? values.description.trim() : '';
          const mergedDescription = currentDescription
            ? (currentDescription.includes(proxyCreateText) ? currentDescription : `${currentDescription}\n${proxyCreateText}`)
            : proxyCreateText;
          formData.set('description', mergedDescription);
        } else {
          console.log('ℹ️ 非客户用户创建产品，需要手动设置customer和created_by字段');
          console.log('ℹ️ 当前用户角色:', user.role);
        }
      } else {
        console.error('❌ 无法获取当前用户信息，请重新登录');
        message.error('无法获取当前用户信息，请重新登录');
        setLoading(false);
        return;
      }

      // 如果有上传的图片，添加到 FormData
      if (uploadedPhoto) {
        formData.append('photo', uploadedPhoto);
        console.log('🖼️ 添加图片文件:', uploadedPhoto.name);
      }

      // 提交前打印 FormData 预览（仅调试用途，不含二进制文件内容）
      try {
        const preview: Record<string, any> = {};
        // 注意：File/Blob 在控制台显示为对象引用
        for (const [k, v] of (formData as any).entries()) {
          preview[k] = v;
        }
        console.log('🧪 FormData 预览:', preview);
      } catch (e) {
        console.warn('⚠️ FormData 预览失败:', e);
      }

      console.log('📤 开始调用创建产品API...');
      // 调用创建产品的 API
      const response = await request.post('/api/v1/products/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('✅ 产品创建成功，API响应:', {
        status: response.status,
        data: response.data
      });
      
      message.success('创建成功');
      
      // 🔑 关键修改：使用页面内状态进行导航
      if (navigationContext.returnPath) {
        console.log('🔄 检测到returnPath，准备返回:', navigationContext.returnPath);
        
        // 将后端返回的数据转换为ProductItem格式
        const productItem = {
          id: response.data.id,
          name: response.data.name,
          sku: response.data.custom_code,
          system_code: response.data.system_code || '',
          sorting_codes: response.data.sorting_codes || [],
          weight_grams: Math.ceil(response.data.weight_grams || 0),
          length: Math.ceil(response.data.length_cm || 0),
          width: Math.ceil(response.data.width_cm || 0),
          height: Math.ceil(response.data.height_cm || 0),
          selected: false
        };

        console.log('📦 转换后的产品项:', productItem);
        console.log('🧭 开始导航回returnPath:', navigationContext.returnPath);

        // 🔑 关键修复：在导航之前记录当前标签页信息
        const tabsManager = (window as any).tabsManager;
        let currentTabKey = null;
        let productCreateTabKey = null;
        
        if (tabsManager && typeof tabsManager.getActiveTab === 'function') {
          currentTabKey = tabsManager.getActiveTab();
          console.log('🔍 记录当前标签页信息:', {
            currentTabKey,
            currentPath: window.location.pathname,
            isProductCreatePage: window.location.pathname.includes('/products/create')
          });
          
          // 如果当前确实是产品创建页面，记录其标签页key
          if (window.location.pathname.includes('/products/create')) {
            productCreateTabKey = currentTabKey;
            console.log('✅ 记录产品创建页面标签页key:', productCreateTabKey);
          }
        }

        // 🔑 关键修复：使用replace模式导航，避免标签页保持打开状态
        navigate(navigationContext.returnPath, {
          state: { 
            newProduct: productItem,
            navigationContext: navigationContext,
            returnTimestamp: new Date().toISOString()
          },
          replace: true // 🔑 关键：使用replace模式，替换当前标签页
        });
        
        console.log('✅ 导航指令已发送');

        // 从“创建项目基础配置”进入时，避免关闭当前标签页导致焦点跳到列表页
        if (isInventoryConfigReturn) {
          console.log('✅ inventory-config 回跳场景：跳过标签页关闭逻辑，确保停留在基础配置创建页');
          return;
        }
        
        // 🔑 关键修复：关闭正确的标签页（产品创建页面，而不是库存配置页面）
        setTimeout(() => {
          console.log('🔒 开始关闭产品创建页面标签页');
          
          if (tabsManager && productCreateTabKey) {
            console.log('✅ 找到产品创建页面标签页key，准备关闭:', productCreateTabKey);
            
            // 方式1：通过具体的标签页key关闭
            const success = tabsManager.closeTab(productCreateTabKey);
            if (success) {
              console.log('✅ 通过标签页key成功关闭产品创建页面标签页');
            } else {
              console.warn('⚠️ 通过标签页key关闭失败，尝试其他方式');
              
              // 方式2：通过路径关闭
              const pathSuccess = tabsManager.closeTabByPath('/dashboard/products/create');
              if (pathSuccess) {
                console.log('✅ 通过路径成功关闭产品创建页面标签页');
              } else {
                console.warn('⚠️ 通过路径关闭也失败');
              }
            }
          } else if (tabsManager && typeof tabsManager.closeCurrentTab === 'function') {
            console.log('⚠️ 没有记录产品创建页面标签页key，使用closeCurrentTab（可能关闭错误页面）');
            const success = tabsManager.closeCurrentTab();
            if (success) {
              console.log('✅ 通过closeCurrentTab关闭标签页（可能关闭了库存配置页面）');
            } else {
              console.warn('⚠️ closeCurrentTab也失败');
            }
          } else {
            console.warn('⚠️ TabsManager API 不可用，无法自动关闭标签页');
          }
        }, 1000); // 延迟1秒，确保导航完成
      } else {
        console.log('ℹ️ 没有returnPath，导航到产品列表页');
        // 否则返回产品列表页（按角色选择正确的后台前缀）
        const basePath = user?.role === 'admin' ? '/dashboard/admin' : (user?.role === 'staff' ? '/dashboard/staff' : '/dashboard/customer');
        
        // 同样记录当前标签页信息
        const tabsManager = (window as any).tabsManager;
        let currentTabKey = null;
        
        if (tabsManager && typeof tabsManager.getActiveTab === 'function') {
          currentTabKey = tabsManager.getActiveTab();
          console.log('🔍 记录当前标签页信息（无returnPath）:', currentTabKey);
        }
        
        navigate(`${basePath}/products/list`);
        
        // 同样尝试关闭当前标签页
        setTimeout(() => {
          if (tabsManager && currentTabKey) {
            // 通过记录的标签页key关闭
            tabsManager.closeTab(currentTabKey);
          } else if (tabsManager && typeof tabsManager.closeCurrentTab === 'function') {
            tabsManager.closeCurrentTab();
          }
        }, 1000);
      }
    } catch (error: any) {
      console.error('❌ 创建产品失败:', error);
      if (error.response?.data) {
        // 显示后端返回的具体错误信息
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          Object.entries(errorData).forEach(([field, messages]) => {
            if (Array.isArray(messages)) {
              message.error(`${field}: ${messages[0]}`);
            }
          });
        } else {
          message.error(errorData);
        }
      } else {
        message.error('创建失败，请稍后重试');
      }
    } finally {
      setLoading(false);
      console.log('🏁 产品创建流程完成');
    }
  };

  // 处理取消（已不在页头显示返回按钮，保留函数以备后用）；编辑模式取消回到产品列表
  const handleCancel = () => {
    if (isEdit) backToProductList(navigate, user?.role);
  };

  // 🔑 修复：导航完成后的状态检查（优化replace模式检查）
  useEffect(() => {
    // 监听导航完成
    const handleNavigationComplete = () => {
      console.log(' [ProductCreate] 导航完成检查:', {
        currentPath: window.location.pathname,
        expectedPath: navigationContext.returnPath,
        navigationSuccessful: window.location.pathname === navigationContext.returnPath,
        isReplaceMode: true, // 🔑 标记为replace模式
        timestamp: new Date().toISOString()
      });
      
      // 🔑 关键修复：检查replace模式是否成功
      if (window.location.pathname === navigationContext.returnPath) {
        console.log('✅ replace模式导航成功，当前标签页已替换为库存配置页面');
      } else {
        console.log('⚠️ replace模式导航可能未完全成功，当前路径:', window.location.pathname);
      }
    };
    
    // 🔑 关键修复：增加延迟时间，确保replace模式完全生效
    const timer = setTimeout(handleNavigationComplete, 300);
    
    return () => clearTimeout(timer);
  }, [navigationContext.returnPath]);

  // 🔑 新增：页面卸载时的状态清理
  useEffect(() => {
    return () => {
      // 如果页面被卸载但没有成功导航，记录日志
      if (navigationContext.returnPath) {
        console.log(' [ProductCreate] 页面卸载，导航上下文:', {
          returnPath: navigationContext.returnPath,
          source: navigationContext.source,
          timestamp: navigationContext.timestamp
        });
      }
    };
  }, [navigationContext.returnPath, navigationContext.source, navigationContext.timestamp]);

  // 处理图片上传
  const handleUpload: UploadProps['customRequest'] = async ({ file, onSuccess, onError }) => {
    try {
      const formData = new FormData();
      formData.append('photo', file as File);
      
      const response = await request.post('/api/v1/products/upload/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.url) {
        setImageUrl(response.data.url);
        setUploadedPhoto(file as File);
        onSuccess?.(response.data);
        message.success('图片上传成功');
      } else {
        throw new Error('上传响应中没有图片 URL');
      }
    } catch (error: any) {
      console.error('图片上传失败:', error);
      message.error(error.response?.data?.error || '图片上传失败');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  // 上传配置
  const uploadProps: UploadProps = {
    name: 'photo',
    listType: 'picture-card',
    showUploadList: false,
    customRequest: handleUpload,
    beforeUpload: (file) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过 2MB！');
        return false;
      }
      return true;
    }
  };

  return (
    <div className="p-6">
      <div className={isEdit ? 'mb-6 flex flex-wrap items-baseline gap-x-6 gap-y-1' : 'mb-6'}>
        <h1 className="text-2xl font-medium" style={isEdit ? { marginBottom: 0 } : undefined}>{isEdit ? '编辑产品' : '创建新产品'}</h1>
        {/* 编辑模式：标题行显示产品名称和系统编码（加载时的信息，不随表单输入变化） */}
        {isEdit && editingProduct ? (
          <span className="product-edit-title-info flex flex-wrap items-baseline gap-x-4 text-sm text-gray-600">
            <span>
              产品名称: <span className="text-gray-900">{editingProduct.name}</span>
            </span>
            <span>
              系统编码: <span style={{ color: '#1677ff' }}>{editingProduct.system_code}</span>
            </span>
          </span>
        ) : null}
      </div>

      {editLimited && editingProduct?.edit_lock_reason ? (
        <Alert className="mb-4 product-edit-limited-alert" type="warning" showIcon message={editingProduct.edit_lock_reason} />
      ) : null}

      {/* 表单卡片 */}
      <Spin spinning={isEdit && loadingProduct}>
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="max-w-4xl mx-auto"
        >
          {/* 基本信息 */}
          <div className="mb-8">
            <h2 className="text-lg font-medium mb-4">基本信息</h2>
            {needCustomerField && (
              <Form.Item
                name="customer"
                label="所属客户"
                rules={[{ required: true, message: '请选择所属客户' }]}
                className="max-w-xl"
              >
                <Select
                  placeholder="请选择客户主账号"
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  loading={customerOptionsLoading}
                  options={customerOptions}
                />
              </Form.Item>
            )}
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="name"
                label="产品名称"
                rules={[{ required: true, message: '请输入产品名称' }]}
              >
                <Input placeholder="请输入产品名称" maxLength={100} disabled={editLimited} />
              </Form.Item>

              <Form.Item
                name="custom_code"
                label="客户自定义编码"
                rules={[
                  { required: true, message: '请输入客户自定义编码 允许任意字符组合' }
                ]}
              >
                <Input placeholder="请输入客户自定义编码 允许任意字符组合" maxLength={50} disabled={editLimited} />
              </Form.Item>

              <Form.List name="sorting_codes">
                {(fields, { add, remove }) => (
                  <div className="col-span-2">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <label className="block text-sm font-medium text-gray-700 mb-0">分拣条码</label>
                        <HelpHintButton onClick={showSortingBarcodeHelpModal}>说明</HelpHintButton>
                      </div>
                      <Button
                        type="link"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      >
                        添加分拣条码
                      </Button>
                    </div>
                    {fields.map(({ key, name, ...restField }) => {
                      // 已进入业务的产品：原有分拣条码不能修改、删除，只能新增
                      const lockedCode = editLimited && Boolean(form.getFieldValue(['sorting_codes', name, 'existing']));
                      return (
                      <div key={key} className="flex gap-4 mb-4 items-start">
                        <Form.Item
                          {...restField}
                          name={[name, 'code']}
                          rules={[
                            { max: 50, message: '分拣条码不能超过50个字符' }
                          ]}
                          className="mb-0 w-[208px] shrink-0 sm:w-[248px]"
                        >
                          <Input placeholder="请输入分拣条码" disabled={lockedCode} />
                        </Form.Item>
                        <div className="product-create-sorting-desc min-w-0 flex-1 max-w-lg">
                          <Form.Item
                            {...restField}
                            name={[name, 'description']}
                            className="mb-0 w-full"
                          >
                            <Input.TextArea
                              className="min-w-0"
                              placeholder="请输入条码说明(选填),如本条码应用于哪个平台或者哪个店铺等信息"
                              autoSize={{ minRows: 1, maxRows: 3 }}
                            />
                          </Form.Item>
                        </div>
                        <Button
                          type="link"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          disabled={lockedCode}
                          aria-label="删除分拣条码"
                        />
                      </div>
                      );
                    })}
                    {fields.length === 0 && (
                      <div className="text-gray-400 text-center py-4 bg-gray-50 rounded-md">
                        点击"添加分拣条码"按钮来添加产品的分拣条码
                      </div>
                    )}
                  </div>
                )}
              </Form.List>

              <Form.Item
                name="description"
                label="产品描述"
                className="col-span-2"
              >
                <TextArea
                  placeholder="请输入产品描述"
                  maxLength={500}
                  showCount
                  rows={4}
                />
              </Form.Item>
            </div>
          </div>

          {/* 产品属性 */}
          <div className="mb-8">
            <h2 className="text-lg font-medium mb-4">产品属性</h2>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                name="value_usd"
                label="价值(USD)"
                rules={[
                  { required: true, message: '请输入产品价值' },
                  { type: 'number', min: 0, message: '价值必须大于等于0' }
                ]}
              >
                <InputNumber
                  placeholder="请输入产品价值"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  disabled={editLimited}
                />
              </Form.Item>

              <Form.Item
                name="weight_grams"
                label="重量(克)"
                rules={[
                  { required: true, message: '请输入产品重量' },
                  { type: 'number', min: 0, message: '重量必须大于等于0' }
                ]}
              >
                <InputNumber
                  placeholder="请输入产品重量"
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  disabled={editLimited}
                />
              </Form.Item>

              <div className="col-span-2 grid grid-cols-3 gap-4">
                <Form.Item
                  name="length_cm"
                  label="长度(厘米)"
                  rules={[
                    { required: true, message: '请输入产品长度' },
                    { type: 'number', min: 0, message: '长度必须大于等于0' },
                  ]}
                  className="mb-0"
                >
                  <InputNumber
                    placeholder="长度"
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    disabled={editLimited}
                  />
                </Form.Item>
                <Form.Item
                  name="width_cm"
                  label="宽度(厘米)"
                  rules={[
                    { required: true, message: '请输入产品宽度' },
                    { type: 'number', min: 0, message: '宽度必须大于等于0' },
                  ]}
                  className="mb-0"
                >
                  <InputNumber
                    placeholder="宽度"
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    disabled={editLimited}
                  />
                </Form.Item>
                <Form.Item
                  name="height_cm"
                  label="高度(厘米)"
                  rules={[
                    { required: true, message: '请输入产品高度' },
                    { type: 'number', min: 0, message: '高度必须大于等于0' },
                  ]}
                  className="mb-0"
                >
                  <InputNumber
                    placeholder="高度"
                    min={0}
                    precision={2}
                    style={{ width: '100%' }}
                    disabled={editLimited}
                  />
                </Form.Item>
              </div>

              <Form.Item label="自带物流包装" className="col-span-2">
                <Space align="center" size="middle" wrap>
                  <Form.Item name="has_logistics_package" valuePropName="checked" noStyle>
                    <Switch disabled={editLimited} />
                  </Form.Item>
                  <HelpHintButton onClick={showLogisticsPackageHelpModal}>说明</HelpHintButton>
                </Space>
              </Form.Item>
            </div>
          </div>

          {/* 产品图片 */}
          <div className="mb-8">
            <h2 className="text-lg font-medium mb-1">产品图片</h2>
            <p className="text-sm text-gray-500 mb-4">仅需上传一张主图</p>
            <Form.Item name="photo">
              <Upload {...uploadProps}>
                {imageUrl ? (
                  <img src={imageUrl} alt="产品照片" style={{ width: '100%' }} />
                ) : (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>上传</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </div>

          {/* 表单操作按钮 */}
          <div className="flex items-center w-full gap-3 flex-wrap">
            <div className="flex-1 min-w-0" />
            <div
              className="flex items-center gap-2 shrink-0 text-sm"
              style={{ color: '#d4380d' }}
            >
              <ExclamationCircleFilled style={{ color: '#fa541c', fontSize: 18 }} aria-hidden />
              <span className="product-form-notice">
                {!isEdit
                  ? '请确保信息准确,产品被使用后部分信息将无法修改'
                  : editLimited
                    ? '已进入业务的产品只能修改产品描述、产品图片和新增分拣条码'
                    : editingProduct?.usage_level === 'configured'
                      ? '该产品已用于项目基础配置,修改产品名称或分拣条码后,已打印的库存单元标签需要重新打印'
                      : '请确保信息准确,产品被使用后部分信息将无法修改'}
              </span>
            </div>
            <div className="flex-1 flex justify-end gap-4 min-w-[200px]">
              <Button onClick={handleCancel}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading} disabled={isEdit && !editingProduct}>
                {isEdit ? '保存' : '创建'}
              </Button>
            </div>
          </div>
        </Form>
      </Card>
      </Spin>
    </div>
  );
};

export default CreateProduct; 